import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Car, Loader2, History, Check, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';

const fmt = (v) => (v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v) : '—');

function Item({ it, onDone }) {
    const [plate, setPlate] = useState(it.immatriculation_detectee || '');
    const [choices, setChoices] = useState(null);
    const link = async (body) => {
        try {
            const r = await axios.post(`${API_URL}/api/vehicle-documents/link`, { scan_result_id: it.id, ...body }, { withCredentials: true });
            if (r.data.status === 'choix_requis') { setChoices(r.data.vehicles); return; }
            toast.success(`Rattaché à ${r.data.vehicle_nom || 'ce dossier'}`); onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    const confirm = async (c) => {
        try { await axios.post(`${API_URL}/api/vehicle-documents/${c.link_id}/confirm`, {}, { withCredentials: true }); toast.success('Rattachement confirmé'); onDone(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    const cands = choices ? choices.map((v) => ({ ...v, vehicle_id: v.id })) : it.candidats;
    return (
        <div className="p-3 rounded-xl border border-gray-100 bg-white" data-testid={`identify-item-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{it.fournisseur || 'Facture'}{it.numero_facture ? ` · N° ${it.numero_facture}` : ''}</p>
                    <p className="text-xs text-gray-500">{it.date_facture || '—'} · {it.lien_vehicule === 'a_confirmer' ? 'À confirmer' : 'Véhicule à identifier'}{it.plaque_fiable === false ? ' · plaque douteuse' : ''}</p>
                </div>
                <span className="text-sm font-mono font-semibold shrink-0">{fmt(it.montant_ttc)}</span>
            </div>
            {cands?.length > 0 && (
                <div className="mt-2 space-y-1">
                    {cands.map((c) => (
                        <button key={c.vehicle_id} onClick={() => (c.link_id ? confirm(c) : link({ vehicle_id: c.vehicle_id }))}
                            className="w-full flex items-center justify-between gap-2 text-left text-xs px-3 py-2 rounded-lg bg-gray-50 hover:bg-emerald-50" data-testid={`identify-choose-${it.id}-${c.vehicle_id}`}>
                            <span className="truncate">{c.nom} · <span className="font-mono">{c.immatriculation}</span> · {c.date_vente ? 'Vendu' : 'Actif'}</span>
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        </button>
                    ))}
                </div>
            )}
            <div className="flex gap-2 mt-2">
                <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="AB-123-CD" className="h-9 text-sm font-mono uppercase" data-testid={`identify-plate-${it.id}`} />
                <Button onClick={() => link({ immatriculation: plate })} disabled={!plate.trim()} className="h-9 px-3 text-xs bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid={`identify-link-${it.id}`}>Rattacher</Button>
            </div>
        </div>
    );
}

export const DocumentsToIdentify = () => {
    const [data, setData] = useState({ items: [], count: 0 });
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [est, setEst] = useState(null);

    const load = useCallback(() => {
        axios.get(`${API_URL}/api/vehicle-documents/a-identifier`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
        axios.get(`${API_URL}/api/vehicle-documents/reanalyse-estimation`, { withCredentials: true }).then((r) => setEst(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const retro = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API_URL}/api/vehicle-documents/retro`, {}, { withCredentials: true });
            toast.success(`Reprise terminée : ${r.data.rattachees} rattachée(s), ${r.data.a_confirmer || 0} à confirmer, ${r.data.a_identifier || 0} à identifier`);
            load(); setOpen(true);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur reprise'); }
        setBusy(false);
    };
    const reanalyse = async () => {
        if (!window.confirm(`Relire les plaques de ${Math.min(est.documents, est.par_lot)} document(s) par IA ?\nCoût estimé : ${(Math.min(est.documents, est.par_lot) * est.cout_unitaire_eur).toFixed(2)} €.\nAucun frais ne sera créé.`)) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API_URL}/api/vehicle-documents/reanalyse`, { confirm: true }, { withCredentials: true });
            toast.success(`${r.data.documents} document(s) relus : ${r.data.rattaches} rattachement(s), ${r.data.a_confirmer} à confirmer`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur relecture'); }
        setBusy(false);
    };

    return (
        <div className="card p-4 sm:p-5 mb-6" data-testid="identify-panel">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left" data-testid="identify-toggle">
                    <Car className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-gray-900">Véhicule à identifier</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="identify-count">{data.count}</span>
                    {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={retro} disabled={busy} className="h-9 text-xs" data-testid="identify-retro-btn">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <History className="w-3.5 h-3.5 mr-1.5" />}Rattacher les factures existantes
                    </Button>
                    {est?.documents > 0 && (
                        <Button variant="outline" onClick={reanalyse} disabled={busy} className="h-9 text-xs" data-testid="identify-reanalyse-btn">
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Relire {Math.min(est.documents, est.par_lot)} doc. (~{(Math.min(est.documents, est.par_lot) * est.cout_unitaire_eur).toFixed(2)} €)
                        </Button>
                    )}
                </div>
            </div>
            {open && (
                <div className="mt-4 space-y-2">
                    {data.items.length === 0
                        ? <p className="text-sm text-gray-400 text-center py-4">Toutes les factures sont rattachées.</p>
                        : data.items.map((it) => <Item key={it.id} it={it} onDone={load} />)}
                </div>
            )}
        </div>
    );
};
